<template>
  <div
    class="full f-col"
    :class="brandClasses[type]?.layoutContainer"
    data-id="dialog-layout"
    data-test-id="vf-dialog-layout"
  >
    <!-- Header -->
    <div
      v-show="!hideHeader"
      class="w-full flex items-center gap-2"
      :class="[brandClasses[type]?.header, { 'justify-end': !$slots.title }]"
    >
      <slot name="before-title" />
      <base-dialog-title v-if="$slots.title" class="w-full truncate">
        <slot name="title" />
      </base-dialog-title>
      <slot name="after-title" />
      <base-dialog-close
        v-if="!hideCloseButton"
        ref="closeRef"
        :aria-label="$t.close"
        :class="[brandClasses[type]?.close, classClose]"
        data-test-id="vf-dialog-close"
        @click="emit('close')"
      >
        <vf-icon name="close" :size="closeIconSize" />
      </base-dialog-close>
    </div>
    <div
      class="grow"
      :class="[{ 'overflow-y-auto': type === 'panel' }, brandClasses[type]?.wrapper, classWrapper]"
    >
      <slot />
    </div>
    <!-- Footer -->
    <div v-if="$slots.footer" :class="[brandClasses[type]?.footer, classFooter]">
      <slot name="footer" />
    </div>
    <base-button
      v-if="!(hideHeader || hideCloseButton)"
      class="sr-only"
      tabindex="0"
      @focus="closeRef?.$el.focus()"
    >
      {{ $t.close }}
    </base-button>
  </div>
</template>

<script lang="ts" setup>
import type { CSSClass } from '#types/common'

defineProps<{
  /**
   * Classes to be applied to the close button
   */
  classClose?: CSSClass
  /**
   * Classes to be applied to the footer
   */
  classFooter?: CSSClass
  /**
   * Classes to be applied to the main content wrapper
   * Note: this prop can't be named classContent because
   * this name is already in use in the parent component scope
   * that is automatically inherited by this component
   */
  classWrapper?: CSSClass
  hideCloseButton?: boolean
  hideHeader?: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

defineSlots<{
  'after-title': void
  'before-title': void
  'default': void
  'footer': void
  'title': void
}>()

const { type } = inject(InjectKeyDialog)!

const { brandClasses, closeIconSize } = useAppConfig().components.vf.dialog
const closeRef = useTemplateRef('closeRef')
</script>
